/**
 * Authored bitmap glyph data. Two designs are drawn by hand; the larger faces
 * are integer scales of the Body design (doc 17 §1), which is exactly why they
 * stay perfectly on the pixel grid at every UI scale.
 *
 *   MICRO  3x5 ink box  — authored here
 *   BODY   5x7 caps + 2 descender rows — authored here
 *   HEADER = BODY x2
 *   DISPLAY = BODY x3, horizontally dilated to a display weight
 *
 * Rows are strings: '#' is ink, anything else is transparent. A glyph's width is
 * its row length, so spacing is proportional; digits are forced to a common
 * advance at render time so stat columns line up (doc 17 §8).
 */

export type GlyphMap = Record<string, string[]>;

/** BODY — cap height 7, descenders reach rows 7-8. */
export const BODY_GLYPHS: GlyphMap = {
  ' ': ['...', '...', '...', '...', '...', '...', '...'],

  A: ['.###.', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  B: ['####.', '#...#', '#...#', '####.', '#...#', '#...#', '####.'],
  C: ['.###.', '#...#', '#....', '#....', '#....', '#...#', '.###.'],
  D: ['####.', '#...#', '#...#', '#...#', '#...#', '#...#', '####.'],
  E: ['####', '#...', '#...', '###.', '#...', '#...', '####'],
  F: ['####', '#...', '#...', '###.', '#...', '#...', '#...'],
  G: ['.###.', '#...#', '#....', '#.###', '#...#', '#...#', '.###.'],
  H: ['#...#', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  I: ['###', '.#.', '.#.', '.#.', '.#.', '.#.', '###'],
  J: ['...#', '...#', '...#', '...#', '#..#', '#..#', '.##.'],
  K: ['#...#', '#..#.', '#.#..', '##...', '#.#..', '#..#.', '#...#'],
  L: ['#...', '#...', '#...', '#...', '#...', '#...', '####'],
  M: ['#...#', '##.##', '#.#.#', '#.#.#', '#...#', '#...#', '#...#'],
  N: ['#...#', '##..#', '#.#.#', '#.#.#', '#..##', '#...#', '#...#'],
  O: ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  P: ['####.', '#...#', '#...#', '####.', '#....', '#....', '#....'],
  Q: ['.###.', '#...#', '#...#', '#...#', '#.#.#', '#..#.', '.##.#'],
  R: ['####.', '#...#', '#...#', '####.', '#.#..', '#..#.', '#...#'],
  S: ['.####', '#....', '#....', '.###.', '....#', '....#', '####.'],
  T: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
  U: ['#...#', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  V: ['#...#', '#...#', '#...#', '#...#', '#...#', '.#.#.', '..#..'],
  W: ['#...#', '#...#', '#...#', '#.#.#', '#.#.#', '##.##', '#...#'],
  X: ['#...#', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '#...#'],
  Y: ['#...#', '#...#', '.#.#.', '..#..', '..#..', '..#..', '..#..'],
  Z: ['#####', '....#', '...#.', '..#..', '.#...', '#....', '#####'],

  a: ['....', '....', '.##.', '...#', '.###', '#..#', '.###'],
  b: ['#...', '#...', '###.', '#..#', '#..#', '#..#', '###.'],
  c: ['....', '....', '.###', '#...', '#...', '#...', '.###'],
  d: ['...#', '...#', '.###', '#..#', '#..#', '#..#', '.###'],
  e: ['....', '....', '.##.', '#..#', '####', '#...', '.###'],
  f: ['.##', '#..', '###', '#..', '#..', '#..', '#..'],
  g: ['....', '....', '.###', '#..#', '#..#', '#..#', '.###', '...#', '###.'],
  h: ['#...', '#...', '###.', '#..#', '#..#', '#..#', '#..#'],
  i: ['#', '.', '#', '#', '#', '#', '#'],
  j: ['..#', '...', '..#', '..#', '..#', '..#', '..#', '..#', '##.'],
  k: ['#...', '#...', '#..#', '#.#.', '##..', '#.#.', '#..#'],
  l: ['##.', '.#.', '.#.', '.#.', '.#.', '.#.', '.##'],
  m: ['.....', '.....', '##.#.', '#.#.#', '#.#.#', '#.#.#', '#.#.#'],
  n: ['....', '....', '###.', '#..#', '#..#', '#..#', '#..#'],
  o: ['....', '....', '.##.', '#..#', '#..#', '#..#', '.##.'],
  p: ['....', '....', '###.', '#..#', '#..#', '#..#', '###.', '#...', '#...'],
  q: ['....', '....', '.###', '#..#', '#..#', '#..#', '.###', '...#', '...#'],
  r: ['...', '...', '#.#', '##.', '#..', '#..', '#..'],
  s: ['....', '....', '.###', '#...', '.##.', '...#', '###.'],
  t: ['.#.', '.#.', '###', '.#.', '.#.', '.#.', '..#'],
  u: ['....', '....', '#..#', '#..#', '#..#', '#..#', '.###'],
  v: ['.....', '.....', '#...#', '#...#', '#...#', '.#.#.', '..#..'],
  w: ['.....', '.....', '#...#', '#.#.#', '#.#.#', '#.#.#', '.#.#.'],
  x: ['...', '...', '#.#', '#.#', '.#.', '#.#', '#.#'],
  y: ['....', '....', '#..#', '#..#', '#..#', '#..#', '.###', '...#', '###.'],
  z: ['....', '....', '####', '...#', '..#.', '.#..', '####'],

  '0': ['.###.', '#...#', '#..##', '#.#.#', '##..#', '#...#', '.###.'],
  '1': ['..#..', '.##..', '..#..', '..#..', '..#..', '..#..', '.###.'],
  '2': ['.###.', '#...#', '....#', '...#.', '..#..', '.#...', '#####'],
  '3': ['####.', '....#', '....#', '.###.', '....#', '....#', '####.'],
  '4': ['...#.', '..##.', '.#.#.', '#..#.', '#####', '...#.', '...#.'],
  '5': ['#####', '#....', '####.', '....#', '....#', '#...#', '.###.'],
  '6': ['..##.', '.#...', '#....', '####.', '#...#', '#...#', '.###.'],
  '7': ['#####', '....#', '...#.', '..#..', '.#...', '.#...', '.#...'],
  '8': ['.###.', '#...#', '#...#', '.###.', '#...#', '#...#', '.###.'],
  '9': ['.###.', '#...#', '#...#', '.####', '....#', '...#.', '.##..'],

  '!': ['#', '#', '#', '#', '#', '.', '#'],
  '"': ['#.#', '#.#', '...', '...', '...', '...', '...'],
  '#': ['.....', '.#.#.', '#####', '.#.#.', '#####', '.#.#.', '.....'],
  $: ['..#..', '.####', '#.#..', '.###.', '..#.#', '####.', '..#..'],
  '%': ['##..#', '##..#', '...#.', '..#..', '.#...', '#..##', '#..##'],
  '&': ['.##..', '#..#.', '#.#..', '.#...', '#.#.#', '#..#.', '.##.#'],
  "'": ['#', '#', '.', '.', '.', '.', '.'],
  '(': ['.#', '#.', '#.', '#.', '#.', '#.', '.#'],
  ')': ['#.', '.#', '.#', '.#', '.#', '.#', '#.'],
  '*': ['...', '#.#', '.#.', '###', '.#.', '#.#', '...'],
  '+': ['...', '...', '...', '.#.', '###', '.#.', '...'],
  ',': ['..', '..', '..', '..', '..', '..', '.#', '#.'],
  '-': ['...', '...', '...', '###', '...', '...', '...'],
  '—': ['.....', '.....', '.....', '#####', '.....', '.....', '.....'], // em dash
  '–': ['....', '....', '....', '####', '....', '....', '....'], // en dash
  '.': ['.', '.', '.', '.', '.', '.', '#'],
  '/': ['..#', '..#', '.#.', '.#.', '.#.', '#..', '#..'],
  ':': ['.', '.', '#', '.', '.', '#', '.'],
  ';': ['..', '..', '.#', '..', '..', '.#', '#.'],
  '<': ['...', '..#', '.#.', '#..', '.#.', '..#', '...'],
  '=': ['...', '...', '###', '...', '###', '...', '...'],
  '>': ['...', '#..', '.#.', '..#', '.#.', '#..', '...'],
  '?': ['.###.', '#...#', '....#', '...#.', '..#..', '.....', '..#..'],
  '@': ['.###.', '#...#', '#.###', '#.#.#', '#.###', '#....', '.###.'],
  '[': ['##', '#.', '#.', '#.', '#.', '#.', '##'],
  '\\': ['#..', '#..', '.#.', '.#.', '.#.', '..#', '..#'],
  ']': ['##', '.#', '.#', '.#', '.#', '.#', '##'],
  '^': ['.#.', '#.#', '...', '...', '...', '...', '...'],
  _: ['.....', '.....', '.....', '.....', '.....', '.....', '.....', '#####'],
  '`': ['#.', '.#', '..', '..', '..', '..', '..'],
  '{': ['.##', '.#.', '.#.', '#..', '.#.', '.#.', '.##'],
  '|': ['#', '#', '#', '#', '#', '#', '#'],
  '}': ['##.', '.#.', '.#.', '..#', '.#.', '.#.', '##.'],
  '~': ['.....', '.....', '.#..#', '#.#.#', '#..#.', '.....', '.....'],

  // Game glyphs — pixel-authored, never emoji (doc 10).
  '★': ['.....', '..#..', '.###.', '#####', '.###.', '#.#.#', '.....'], // star, menace rating
  '·': ['.', '.', '.', '#', '.', '.', '.'], // middot separator
  '●': ['.....', '.###.', '#####', '#####', '#####', '.###.', '.....'], // full pip
  '○': ['.....', '.###.', '#...#', '#...#', '#...#', '.###.', '.....'], // empty pip
  '◉': ['.....', '.###.', '#...#', '#.#.#', '#...#', '.###.', '.....'], // boss pip
  '⚡': ['..#', '.#.', '##.', '###', '.##', '.#.', '#..'], // power
  '✗': ['.....', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '.....'], // no / blocked
  '→': ['.....', '..#..', '...#.', '#####', '...#.', '..#..', '.....'], // arrow
  '▓': ['#.#.', '.#.#', '#.#.', '.#.#', '#.#.', '.#.#', '#.#.'], // bar fill
  '░': ['#...', '..#.', '#...', '..#.', '#...', '..#.', '#...'], // bar empty
};

/** MICRO — 3x5 ink box, caps only; lowercase renders as small caps. */
export const MICRO_GLYPHS: GlyphMap = {
  ' ': ['..', '..', '..', '..', '..'],

  A: ['.#.', '#.#', '###', '#.#', '#.#'],
  B: ['##.', '#.#', '##.', '#.#', '##.'],
  C: ['.##', '#..', '#..', '#..', '.##'],
  D: ['##.', '#.#', '#.#', '#.#', '##.'],
  E: ['###', '#..', '##.', '#..', '###'],
  F: ['###', '#..', '##.', '#..', '#..'],
  G: ['.##', '#..', '#.#', '#.#', '.##'],
  H: ['#.#', '#.#', '###', '#.#', '#.#'],
  I: ['###', '.#.', '.#.', '.#.', '###'],
  J: ['..#', '..#', '..#', '#.#', '.#.'],
  K: ['#.#', '#.#', '##.', '#.#', '#.#'],
  L: ['#..', '#..', '#..', '#..', '###'],
  M: ['#.#', '###', '###', '#.#', '#.#'],
  N: ['#.#', '##.', '###', '.##', '#.#'],
  O: ['###', '#.#', '#.#', '#.#', '###'],
  P: ['##.', '#.#', '##.', '#..', '#..'],
  Q: ['###', '#.#', '#.#', '###', '..#'],
  R: ['##.', '#.#', '##.', '#.#', '#.#'],
  S: ['.##', '#..', '.#.', '..#', '##.'],
  T: ['###', '.#.', '.#.', '.#.', '.#.'],
  U: ['#.#', '#.#', '#.#', '#.#', '###'],
  V: ['#.#', '#.#', '#.#', '#.#', '.#.'],
  W: ['#.#', '#.#', '###', '###', '#.#'],
  X: ['#.#', '#.#', '.#.', '#.#', '#.#'],
  Y: ['#.#', '#.#', '.#.', '.#.', '.#.'],
  Z: ['###', '..#', '.#.', '#..', '###'],

  '0': ['###', '#.#', '#.#', '#.#', '###'],
  '1': ['.#.', '##.', '.#.', '.#.', '###'],
  '2': ['##.', '..#', '.#.', '#..', '###'],
  '3': ['##.', '..#', '.#.', '..#', '##.'],
  '4': ['#.#', '#.#', '###', '..#', '..#'],
  '5': ['###', '#..', '##.', '..#', '##.'],
  '6': ['.##', '#..', '###', '#.#', '###'],
  '7': ['###', '..#', '.#.', '#..', '#..'],
  '8': ['###', '#.#', '###', '#.#', '###'],
  '9': ['###', '#.#', '###', '..#', '##.'],

  '.': ['.', '.', '.', '.', '#'],
  ',': ['..', '..', '..', '.#', '#.'],
  ':': ['.', '#', '.', '#', '.'],
  '-': ['...', '...', '###', '...', '...'],
  '—': ['....', '....', '####', '....', '....'],
  '–': ['...', '...', '###', '...', '...'],
  '+': ['...', '.#.', '###', '.#.', '...'],
  '/': ['..#', '..#', '.#.', '#..', '#..'],
  '%': ['#.#', '..#', '.#.', '#..', '#.#'],
  '!': ['#', '#', '#', '.', '#'],
  '?': ['##.', '..#', '.#.', '...', '.#.'],
  '(': ['.#', '#.', '#.', '#.', '.#'],
  ')': ['#.', '.#', '.#', '.#', '#.'],
  '[': ['##', '#.', '#.', '#.', '##'],
  ']': ['##', '.#', '.#', '.#', '##'],
  '=': ['...', '###', '...', '###', '...'],
  '★': ['.#.', '###', '###', '#.#', '...'],
  '·': ['.', '.', '#', '.', '.'],
  '●': ['...', '.#.', '###', '.#.', '...'],
  '○': ['...', '.#.', '#.#', '.#.', '...'],
  '⚡': ['..#', '.##', '##.', '.#.', '#..'],
  '✗': ['#.#', '.#.', '.#.', '.#.', '#.#'],
  '→': ['...', '..#', '###', '..#', '...'],
};
